import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseURL from '../assets/contants/BaseURL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faLock,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load saved credentials on component mount - ONLY ONCE
  useEffect(() => {
    const savedUser = localStorage.getItem('rememberedUser');
    const savedOrgId = localStorage.getItem('rememberedOrgId');
    if (savedUser) {
      setUsername(savedUser);
      setRememberMe(true);
    }
    if (savedOrgId) {
      setOrganizationId(savedOrgId);
    }
  }, []);

  const saveCredentials = (username, orgId) => {
    try {
      localStorage.setItem('rememberedUser', username);
      if (orgId) {
        localStorage.setItem('rememberedOrgId', orgId);
      } else {
        localStorage.removeItem('rememberedOrgId');
      }
    } catch (e) {
      console.error('Could not save credentials');
    }
  };

  const clearSavedCredentials = () => {
    try {
      localStorage.removeItem('rememberedUser');
      localStorage.removeItem('rememberedOrgId');
    } catch (e) {
      console.error('Could not clear credentials');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      setErrorMessage('Please enter both username and password');
      return;
    }

    if (!organizationId) {
      setErrorMessage('Please enter organization ID');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    const requestBody = {
      username: username,
      password: password,
      organization_id: organizationId
    };

    try {
      const response = await fetch(`${BaseURL}Userauth/weblogin/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      
      if (data.status === 'OK') {
        sessionStorage.setItem('Token', data.token);
        sessionStorage.setItem('isSuperuser', data.is_superuser);
        sessionStorage.setItem('username', data.username);
        sessionStorage.setItem('session_id', data.session_id);
        sessionStorage.setItem('organization_id', data.organization_id);
        sessionStorage.setItem('organization_email', data.organization_email);
        sessionStorage.setItem('requested_organization_id', data.requested_organization_id || organizationId);

        if (rememberMe) {
          saveCredentials(username, organizationId);
        } else {
          clearSavedCredentials();
        }

        // Navigate to dashboard
        navigate('/dashboard'); 
      } else {
        setErrorMessage(data.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage(error.message || 'Invalid username or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '450px',
      padding: '40px',
      textAlign: 'center',
      margin: '20px'
    },
    logo: {
      marginBottom: '30px'
    },
    logoH2: {
      color: '#2c3e50',
      fontSize: '1.8rem',
      marginBottom: '5px'
    },
    logoP: {
      color: '#2c3e50',
      opacity: 0.7,
      fontSize: '0.9rem'
    },
    form: {
      marginTop: '20px'
    },
    inputGroup: {
      marginBottom: '20px',
      textAlign: 'left'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#2c3e50'
    },
    inputField: {
      position: 'relative'
    },
    icon: {
      position: 'absolute',
      left: '15px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#2c3e50',
      opacity: 0.6,
      zIndex: 1
    },
    input: {
      width: '100%',
      padding: '12px 15px 12px 40px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
      transition: 'all 0.3s',
      boxSizing: 'border-box'
    },
    rememberForgot: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      fontSize: '0.9rem'
    },
    rememberMe: {
      display: 'flex',
      alignItems: 'center'
    },
    checkbox: {
      marginRight: '8px',
      cursor: 'pointer'
    },
    rememberLabel: {
      marginBottom: 0,
      cursor: 'pointer',
      fontWeight: 'normal'
    },
    forgotPassword: {
      textAlign: 'right'
    },
    forgotLink: {
      color: '#3498db',
      textDecoration: 'none'
    },
    button: {
      width: '100%',
      padding: '12px',
      borderRadius: '4px',
      border: 'none',
      backgroundColor: '#3498db',
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed',
      opacity: 0.7
    },
    footer: {
      marginTop: '30px',
      fontSize: '0.9rem',
      color: '#2c3e50',
      opacity: 0.7
    },
    signupSection: {
      marginTop: '20px',
      paddingTop: '20px',
      borderTop: '1px solid #eee',
      fontSize: '0.9rem'
    },
    signupLink: {
      color: '#3498db',
      textDecoration: 'none',
      fontWeight: '600',
      marginLeft: '5px',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit'
    },
    errorMessage: {
      color: '#e74c3c',
      marginBottom: '15px',
      fontSize: '0.9rem',
      padding: '10px',
      backgroundColor: '#fde8e8',
      borderRadius: '4px'
    },
    spinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255,255,255,.3)',
      borderRadius: '50%',
      borderTopColor: '#fff',
      animation: 'spin 1s ease-in-out infinite',
      marginRight: '8px',
      verticalAlign: 'middle'
    },
    helperText: {
      fontSize: '0.8rem',
      color: '#7f8c8d',
      marginTop: '5px',
      marginLeft: '5px'
    }
  };

  // Add global styles for animations
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      input:focus {
        outline: none;
        border-color: #3498db !important;
        box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2) !important;
      }
      
      button:hover:not(:disabled) {
        background-color: #2980b9 !important;
      }
      
      .forgot-link:hover {
        text-decoration: underline !important;
      }
      
      .signup-link:hover {
        text-decoration: underline !important;
        background-color: transparent !important;
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div style={{
      backgroundColor: '#f5f7fa',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      margin: 0,
      padding: '20px'
    }}>
      <div style={styles.container}>
        <div style={styles.logo}>
          <h2 style={styles.logoH2}>CraneMonitor Pro</h2>
          <p style={styles.logoP}>Industrial Crane Monitoring System</p>
        </div>
        
        {errorMessage && (
          <div style={styles.errorMessage}>
            {errorMessage}
          </div>
        )}
        
        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="organizationId">Organization ID *</label>
            <div style={styles.inputField}>
              <FontAwesomeIcon icon={faBuilding} style={styles.icon} />
              <input 
                type="text" 
                id="organizationId" 
                style={styles.input}
                value={organizationId}
                onChange={(e) => {
                  setOrganizationId(e.target.value);
                  setErrorMessage('');
                }}
                placeholder="Enter your organization ID" 
                required 
                autoComplete="organization-id"
              />
            </div>
            <div style={styles.helperText}>
              Enter your unique organization identifier
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="username">Username</label>
            <div style={styles.inputField}>
              <FontAwesomeIcon icon={faUser} style={styles.icon} />
              <input 
                type="text" 
                id="username" 
                style={styles.input}
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setErrorMessage('');
                }}
                placeholder="Enter your username" 
                required 
                autoComplete="username"
              />
            </div>
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="password">Password</label>
            <div style={styles.inputField}>
              <FontAwesomeIcon icon={faLock} style={styles.icon} />
              <input 
                type="password" 
                id="password" 
                style={styles.input}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrorMessage('');
                }}
                placeholder="Enter your password" 
                required 
                autoComplete="current-password"
              />
            </div>
          </div>
          
          <div style={styles.rememberForgot}>
            <div style={styles.rememberMe}>
              <input 
                type="checkbox" 
                id="remember" 
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={styles.checkbox}
              />
              <label style={styles.rememberLabel} htmlFor="remember">Remember me</label>
            </div>
            <div style={styles.forgotPassword}>
              <a
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/forgot-password');
                }}
                style={{...styles.forgotLink, background: 'none', border: 'none', cursor: 'pointer'}}
                className="forgot-link"
                href="#"
              >
                Forgot password?
              </a>
            </div>
          </div>
          
          <button 
            type="submit" 
            style={isLoading ? {...styles.button, ...styles.buttonDisabled} : styles.button}
            disabled={isLoading}
          >
            {isLoading ? (
              <><span style={styles.spinner}></span> Logging in...</>
            ) : (
              <><i className="fas fa-sign-in-alt"></i> Login</>
            )}
          </button>
        </form>
        
        <div style={styles.signupSection}>
          <span>Don't have an account?</span>
          <a
            onClick={(e) => {
              e.preventDefault();
              navigate('/signup');
            }}
            style={styles.signupLink}
            className="signup-link"
            href="#"
          >
            Sign up
          </a>
        </div>
        
        <div style={styles.footer}>
          <p>© 2026 CraneMonitor Pro. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default Login;